import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ShieldCheck, FileText, Zap, Lock, Building2, ScanFace, ChevronRight } from 'lucide-react';
import { CHECKS, SECTORS } from '../data/sectorsData';
import SocialProof from '../components/SocialProof';
import BrandLogo from '../components/BrandLogo';

// Motion primitives — restrained, editorial
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

/**
 * VerifyPro Landing — editorial redesign (Swiss Brutalist × Old Money Tech).
 * - Cream editorial base
 * - Asymmetric bento hero
 * - Restrained navy + orange brand tokens
 * - Cabinet Grotesk display type, IBM Plex body
 */
const Landing = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink font-sans" data-testid="landing-page">
      {/* --- Header ---------------------------------------------------- */}
      <header
        className={`sticky top-0 z-40 transition-all duration-200 ${
          scrolled
            ? 'bg-brand-cream/85 backdrop-blur-md border-b border-brand-ink/10'
            : 'bg-brand-cream border-b border-transparent'
        }`}
        data-testid="landing-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 py-4 flex items-center justify-between">
          <BrandLogo surface="light" showTagline markClass="h-9 w-auto" textClass="text-xl" data-testid="landing-brand-logo" />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-brand-ink/70">
            <a href="#sectors" className="hover:text-brand-ink transition-colors">Sectors</a>
            <a href="#checks" className="hover:text-brand-ink transition-colors">Checks</a>
            <a href="#why" className="hover:text-brand-ink transition-colors">Why VerifyPro</a>
            <a href="#trust" className="hover:text-brand-ink transition-colors">Compliance</a>
          </nav>
          <button
            onClick={() => navigate('/login')}
            className="group inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold text-white bg-brand-navy hover:bg-brand-navy-deep transition-colors"
            data-testid="landing-login-btn"
          >
            Sign in
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </header>

      {/* --- Hero (editorial bento) ------------------------------------ */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 pt-16 lg:pt-24 pb-16 lg:pb-24">
          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            {/* Left: editorial headline (7 cols) */}
            <motion.div
              className="col-span-12 lg:col-span-7 flex flex-col justify-between"
              variants={container}
              initial="hidden"
              animate="show"
            >
              <div>
                <motion.div variants={fadeUp} className="eyebrow mb-8" data-testid="hero-eyebrow">
                  <span className="inline-block w-8 h-px bg-brand-ink/40 mr-3 align-middle" />
                  DPDP-compliant BGV · Every industry · India
                </motion.div>
                <motion.h1
                  variants={fadeUp}
                  className="font-display font-medium text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.95] tracking-tighter text-brand-ink text-balance"
                  data-testid="hero-headline"
                >
                  One platform.{' '}
                  <span className="italic font-normal text-brand-orange">Every</span>{' '}
                  sector.
                </motion.h1>
                <motion.p
                  variants={fadeUp}
                  className="mt-8 max-w-xl text-base lg:text-lg text-brand-ink/70 leading-relaxed text-pretty"
                >
                  From banking and IT to manufacturing, healthcare, retail, logistics and
                  security agencies — VerifyPro runs the same seven background checks with
                  consent-first workflows and audit-grade, QR-verifiable PDF reports.
                  Whatever you hire, however you hire — verify it here.
                </motion.p>
              </div>

              <motion.div variants={fadeUp} className="mt-12 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => navigate('/login')}
                  className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-sm font-semibold text-white bg-brand-orange hover:bg-brand-orange-hover transition-all hover:-translate-y-[1px] shadow-soft"
                  data-testid="hero-cta-primary"
                >
                  Sign in to VerifyPro
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a
                  href="#checks"
                  className="inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold text-brand-ink hover:text-brand-orange transition-colors"
                  data-testid="hero-cta-secondary"
                >
                  See the checks
                  <ChevronRight className="w-4 h-4" />
                </a>
              </motion.div>

              {/* Stats strip */}
              <motion.div variants={fadeUp} className="mt-14 grid grid-cols-3 gap-6 border-t border-brand-ink/10 pt-8" data-testid="hero-stats">
                {[
                  { k: '5', u: 'days', l: 'Median turnaround' },
                  { k: '7', u: 'checks', l: 'End-to-end coverage' },
                  { k: '100%', u: '', l: 'DPDP-compliant' },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-display text-3xl lg:text-4xl font-medium tracking-tight text-brand-ink tabular-nums">
                      {s.k}
                      {s.u && <span className="text-base lg:text-lg text-brand-ink/50 ml-1 font-sans">{s.u}</span>}
                    </div>
                    <div className="mt-1.5 text-xs uppercase tracking-[0.14em] text-brand-ink/50 font-semibold">
                      {s.l}
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right: editorial "monitor" card (5 cols) — sample report preview */}
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 lg:col-span-5 flex items-stretch"
            >
              <div className="relative w-full">
                {/* soft orange glow */}
                <div
                  aria-hidden="true"
                  className="absolute -top-16 -right-16 h-72 w-72 rounded-full pointer-events-none"
                  style={{ background: 'radial-gradient(closest-side, rgba(254,76,28,0.28), transparent 70%)' }}
                />
                <div
                  className="relative bg-brand-navy rounded-md p-6 lg:p-8 text-white overflow-hidden shadow-modal grain-overlay"
                  data-testid="hero-report-card"
                >
                  <div className="flex items-center justify-between">
                    <span className="eyebrow text-white/50">Report Preview</span>
                    <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-mono">
                      VERIFIED
                    </span>
                  </div>
                  <div className="mt-6">
                    <div className="text-[11px] uppercase tracking-widest text-white/40">Report No.</div>
                    <div className="font-mono text-sm text-white/90 mt-0.5">VP-BGV-2026-00042</div>
                  </div>
                  <div className="mt-8 space-y-2.5">
                    {[
                      { k: 'Identity', v: 'Verified', tone: 'ok' },
                      { k: 'Address', v: 'Field-verified', tone: 'ok' },
                      { k: 'Education', v: 'DigiLocker', tone: 'ok' },
                      { k: 'Employment', v: 'HR + EPFO', tone: 'ok' },
                      { k: 'Criminal', v: 'No records', tone: 'ok' },
                    ].map((row, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-white/8 pb-2">
                        <span className="text-sm text-white/80">{row.k}</span>
                        <span className="text-xs font-mono text-emerald-300">{row.v}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 flex items-center justify-between">
                    <div className="text-[11px] text-white/40 uppercase tracking-widest">Risk score</div>
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-3xl font-medium text-brand-orange tabular-nums">96</span>
                      <span className="text-xs text-white/40">/100</span>
                    </div>
                  </div>
                  {/* Bottom row: QR + verify hint */}
                  <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-sm bg-white p-1">
                      <div className="w-full h-full bg-brand-navy grid grid-cols-4 grid-rows-4 gap-px p-1">
                        {[0,1,3,4,5,7,8,10,11,12,14,15].map((i) => (
                          <div key={i} className="bg-white" />
                        ))}
                      </div>
                    </div>
                    <div className="text-[10px] text-white/50 leading-relaxed">
                      Scan to publicly verify report<br />authenticity on any device.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- Marquee trust bar ----------------------------------------- */}
      <section className="border-y border-brand-ink/10 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 py-5 flex items-center gap-8">
          <div className="eyebrow shrink-0 hidden md:block">Trusted across</div>
          <div className="flex-1 flex items-center gap-10 text-sm text-brand-ink/60 whitespace-nowrap overflow-hidden">
            <div className="flex items-center gap-10 animate-marquee">
              {['Banking & NBFC', 'IT & ITES', 'Manufacturing', 'Healthcare', 'Retail & E-commerce', 'Logistics', 'Security & Manpower', 'Corporate & Staffing', 'Insurance', 'Education', 'Hospitality', 'Government & PSU', 'Banking & NBFC', 'IT & ITES', 'Manufacturing', 'Healthcare', 'Retail & E-commerce', 'Logistics', 'Security & Manpower', 'Corporate & Staffing', 'Insurance', 'Education', 'Hospitality', 'Government & PSU'].map((v, i) => (
                <span key={i} className="flex items-center gap-3 font-medium">
                  <span className="w-1 h-1 rounded-full bg-brand-orange" />
                  {v}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- Sectors: one platform for every industry ------------------ */}
      <section id="sectors" className="py-20 lg:py-28 bg-brand-cream">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 lg:gap-8 mb-14">
            <div className="col-span-12 lg:col-span-6">
              <div className="eyebrow mb-4">
                <span className="inline-block w-8 h-px bg-brand-ink/40 mr-3 align-middle" />
                One platform · every industry
              </div>
              <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter text-brand-ink leading-[1.05] text-balance">
                Not built for one industry. Built for how each one hires.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-brand-ink/60 text-base lg:text-lg leading-relaxed text-pretty">
                A bank screening a branch manager, a factory onboarding 300 operators, a
                security agency running bulk guard checks, or a startup hiring its first
                engineer — same consent-first workflow, same evidence-backed report,
                tuned to each sector&apos;s risk profile and volume.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {SECTORS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.button
                  key={i}
                  type="button"
                  onClick={() => navigate(`/sectors/${s.slug}`)}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.45, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -4 }}
                  className="group relative text-left bg-white border border-brand-ink/10 rounded-md p-5 lg:p-6 transition-colors duration-300 hover:border-brand-orange/60"
                  data-testid={`sector-tile-${i + 1}`}
                >
                  <div className="w-10 h-10 rounded-md border border-brand-ink/10 flex items-center justify-center mb-5 group-hover:border-brand-orange group-hover:text-brand-orange transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-base lg:text-lg font-medium tracking-tight text-brand-ink leading-tight">
                    {s.name}
                  </h3>
                  <p className="mt-1.5 text-xs lg:text-sm text-brand-ink/55 leading-relaxed">{s.tileNote}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </motion.button>
              );
            })}
          </div>

          <p className="mt-8 text-sm text-brand-ink/45">
            …and any other sector that hires people. One case file, one report, everywhere.
          </p>
        </div>
      </section>

      {/* --- Social proof: logos + testimonials ------------------------ */}
      <SocialProof />

      {/* --- Why VerifyPro (4 tiles, editorial) ------------------------ */}
      <section id="why" className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 lg:gap-8 mb-14">
            <div className="col-span-12 lg:col-span-5">
              <div className="eyebrow mb-4">
                <span className="inline-block w-8 h-px bg-brand-ink/40 mr-3 align-middle" />
                Why VerifyPro
              </div>
              <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter text-brand-ink leading-[1.05] text-balance">
                Built for compliance teams that can&apos;t afford surprises.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-brand-ink/60 text-base lg:text-lg leading-relaxed text-pretty">
                Every check is documented, timestamped, geo-tagged and cryptographically
                sealed. When an auditor asks how you verified a hire, you have an answer
                that holds up in court.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {[
              { icon: FileText, num: '01', title: 'Comprehensive checks', body: 'Identity, address, education, employment, criminal, reference and credit — one unified case file.' },
              { icon: ScanFace, num: '02', title: 'Audit-grade reports', body: 'Auto-generated PDFs with QR public-verify link, executive summary and DigiLocker appendices.' },
              { icon: Zap, num: '03', title: 'Fast processing', body: 'Automated + manual hybrid delivers a 5-day median TAT versus industry standard of 12–15 days.' },
              { icon: Lock, num: '04', title: 'DPDP compliant', body: 'Consent-first, encrypted at rest, cryptographically sealed evidence, immutable audit trail.' },
            ].map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -4 }}
                  className="group relative bg-brand-cream border border-brand-ink/10 rounded-md p-6 lg:p-7 transition-colors duration-300 hover:border-brand-ink/30"
                  data-testid={`why-tile-${i + 1}`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="w-6 h-6 text-brand-ink" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] tracking-widest text-brand-ink/40">{t.num}</span>
                  </div>
                  <h3 className="font-display text-lg font-medium tracking-tight text-brand-ink mb-2 leading-tight">
                    {t.title}
                  </h3>
                  <p className="text-sm text-brand-ink/60 leading-relaxed">{t.body}</p>
                  <div className="mt-6 h-px bg-brand-ink/10 group-hover:bg-brand-orange transition-colors duration-300" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --- 7 Verification Types (checks) ----------------------------- */}
      <section id="checks" className="py-20 lg:py-28 bg-brand-cream">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 lg:gap-8 mb-14">
            <div className="col-span-12 lg:col-span-5">
              <div className="eyebrow mb-4">
                <span className="inline-block w-8 h-px bg-brand-ink/40 mr-3 align-middle" />
                Seven checks · one platform
              </div>
              <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter text-brand-ink leading-[1.05] text-balance">
                From identity to credit history — everything you need, in one case file.
              </h2>
            </div>
          </div>

          <div className="border border-brand-ink/10 rounded-md bg-white overflow-hidden">
            {CHECKS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className={`group flex items-center gap-6 px-6 lg:px-8 py-5 lg:py-6 transition-colors hover:bg-brand-cream/50 cursor-pointer ${i > 0 ? 'border-t border-brand-ink/10' : ''}`}
                  data-testid={`check-row-${i + 1}`}
                >
                  <div className="font-mono text-[11px] tracking-widest text-brand-ink/40 w-8">
                    0{i + 1}
                  </div>
                  <div className="w-10 h-10 rounded-md border border-brand-ink/10 flex items-center justify-center flex-shrink-0 group-hover:border-brand-orange group-hover:text-brand-orange transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-8">
                    <div className="font-display font-medium text-lg lg:text-xl tracking-tight text-brand-ink flex-shrink-0 sm:w-40">
                      {c.label}
                    </div>
                    <div className="text-sm text-brand-ink/60 mt-1 sm:mt-0">{c.hint}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-brand-ink/30 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --- Trust / CTA ------------------------------------------------ */}
      <section id="trust" className="py-20 lg:py-28 bg-brand-navy text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 h-96 w-96 rounded-full pointer-events-none opacity-40"
          style={{ background: 'radial-gradient(closest-side, rgba(254,76,28,0.45), transparent 70%)' }}
        />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 relative">
          <div className="grid grid-cols-12 gap-6 lg:gap-8 items-end">
            <div className="col-span-12 lg:col-span-7">
              <div className="eyebrow text-white/50 mb-4">
                <span className="inline-block w-8 h-px bg-white/40 mr-3 align-middle" />
                Compliance & trust
              </div>
              <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tighter leading-[1.05] text-balance">
                The evidence trail that stands up to any audit.
              </h2>
              <p className="mt-6 max-w-xl text-white/60 text-base lg:text-lg leading-relaxed">
                Every action is signed, timestamped and preserved. Every consent is recorded.
                Every report can be verified publicly with a single QR scan.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-4 lg:col-start-9">
              <button
                onClick={() => navigate('/login')}
                className="group w-full inline-flex items-center justify-between gap-3 px-6 py-5 rounded-md text-sm font-semibold text-white bg-brand-orange hover:bg-brand-orange-hover transition-all"
                data-testid="cta-primary-bottom"
              >
                <span>Sign in to VerifyPro</span>
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16 pt-10 border-t border-white/10">
            {[
              { icon: ShieldCheck, k: 'DPDP', v: 'Consent-first, encrypted-at-rest' },
              { icon: FileText, k: 'Evidence', v: 'Tamper-evident, court-ready' },
              { icon: Building2, k: 'Any scale', v: 'Startups to enterprises, every sector' },
              { icon: Lock, k: 'Security', v: 'Signed audit trail per action' },
            ].map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-brand-orange mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/40 font-semibold">{t.k}</div>
                    <div className="text-sm text-white/85 mt-0.5">{t.v}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --- Footer ---------------------------------------------------- */}
      <footer className="bg-white border-t border-brand-ink/10 py-10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 flex flex-col sm:flex-row items-center justify-between gap-6 text-sm">
          <BrandLogo surface="light" showTagline markClass="h-8 w-auto" textClass="text-lg" />
          <div className="text-brand-ink/50 text-xs text-center sm:text-right">
            © {new Date().getFullYear()} Infini10 Technologies Pvt. Ltd. · VerifyPro™ is a trademark of Infini10 Technologies ·{' '}
            <button
              onClick={() => navigate('/privacy-policy')}
              className="link-editorial"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
